// src/components/Pentests/RunDetail.js
//
// Detail page for a single tool run. Reached via /pentests/{id}/runs/{kind}/{run_id}.
// Owns the fetch + pagination + auto-poll while running, then dispatches to a
// scanner-specific view component for the body.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  RefreshCw,
  Square,
  Trash2,
  XCircle,
} from 'lucide-react';
import { pentestsAPI, vulnScanAPI } from '../../services/api';
import ConfirmModal from '../common/ConfirmModal';
import { formatAbsoluteDate } from '../../utils/time';

import SubdomainView from './runs/SubdomainView';
import PortScanView from './runs/PortScanView';
import TechFingerprintView from './runs/TechFingerprintView';
import ShodanView from './runs/ShodanView';
import WebCrawlerView from './runs/WebCrawlerView';
import VulnScanView from './runs/VulnScanView';
import ZapView from './runs/ZapView';
import GenericResultsView from './runs/GenericResultsView';
import { RunProgressLine } from './runs/RunProgress';
import { moduleLabel } from './runs/moduleLabels';

const KIND_LABEL = {
  recon: 'Recon',
  port_scan: 'Port Scan',
  vuln_scan: 'Vuln Scan',
};

const STATUS_PILL = {
  pending: { label: 'Pending', color: 'bg-slate-100 text-slate-700', icon: Clock },
  running: { label: 'Running', color: 'bg-cyan-100 text-cyan-700 animate-pulse', icon: Activity },
  completed: { label: 'Completed', color: 'bg-wasabi-100 text-wasabi-700', icon: CheckCircle2 },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700', icon: XCircle },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-700', icon: AlertTriangle },
  timeout: { label: 'Timeout', color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
};

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled', 'timeout']);

function fmt(ts) {
  return ts ? formatAbsoluteDate(ts) : '-';
}

function pickView(run) {
  if (run.kind === 'vuln_scan') return VulnScanView;
  const m = run.module_or_profile;
  if (m === 'subdomain_enum') return SubdomainView;
  if (m === 'port_scan' || m === 'nmap_scan') return PortScanView;
  if (m === 'tech_fingerprint' || m === 'wappalyzer_scan') return TechFingerprintView;
  if (m === 'shodan_scan') return ShodanView;
  if (m === 'web_crawler' || m === 'dir_bruteforce') return WebCrawlerView;
  if (m === 'zap_scan' || m === 'zap') return ZapView;
  return GenericResultsView;
}

export default function RunDetail() {
  const { id, kind, runId } = useParams();
  const navigate = useNavigate();
  const pentestId = Number(id);
  const numericRunId = Number(runId);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const [stopping, setStopping] = useState(false);
  const pollRef = useRef(null);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await pentestsAPI.runs.remove(pentestId, kind, numericRunId);
      navigate(`/pentests/${pentestId}/runs`);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleStop = async () => {
    const vulnScanId = data?.run?.vuln_scan_id;
    if (!vulnScanId) {
      setConfirmStop(false);
      return;
    }
    setStopping(true);
    try {
      await vulnScanAPI.stop(vulnScanId);
      setConfirmStop(false);
      await fetchDetail();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setStopping(false);
    }
  };

  const fetchDetail = useCallback(async (silent = false) => {
    if (!silent) setError(null);
    try {
      const resp = await pentestsAPI.runs.get(pentestId, kind, numericRunId, { page, page_size: pageSize });
      setData(resp);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [pentestId, kind, numericRunId, page, pageSize]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  useEffect(() => {
    const status = data?.run?.status;
    const enrichStatus = data?.run?.scan_metadata?.cve_enrichment_status;
    const enrichInFlight = enrichStatus === 'pending';
    const shouldPoll = (status && !TERMINAL_STATUSES.has(status)) || enrichInFlight;
    if (shouldPoll) {
      pollRef.current = setInterval(() => fetchDetail(true), 4000);
      return () => {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
      };
    }
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [data?.run?.status, data?.run?.scan_metadata?.cve_enrichment_status, fetchDetail]);

  const totalPages = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, Math.ceil(data.results_total / pageSize));
  }, [data, pageSize]);

  if (loading && !data) {
    return <div className="p-8 text-slate-500">Loading run...</div>;
  }
  if (error && !data) {
    return (
      <div className="p-8">
        <div className="mb-4">
          <Link to={`/pentests/${pentestId}/runs`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
            <ArrowLeft className="w-4 h-4" />
            Back to runs
          </Link>
        </div>
        <div className="rounded-xl border border-red-300 bg-red-100 p-4 text-sm text-red-700">{error}</div>
      </div>
    );
  }

  const run = data.run;
  const pill = STATUS_PILL[run.status] || { label: run.status, color: 'bg-slate-100 text-slate-700', icon: Activity };
  const Icon = pill.icon;
  const ViewComponent = pickView(run);
  const canDelete = TERMINAL_STATUSES.has(run.status);
  const canStop = run.kind === 'vuln_scan' && !TERMINAL_STATUSES.has(run.status) && !!run.vuln_scan_id;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <Link
          to={`/pentests/${pentestId}/runs`}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
        >
          <ArrowLeft className="w-4 h-4" />
          All runs
        </Link>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => fetchDetail()}
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-shinobi-green"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
          {canStop && (
            <button
              type="button"
              onClick={() => setConfirmStop(true)}
              title="Stop this scan"
              className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-orange-700"
            >
              <Square className="w-3.5 h-3.5" />
              Stop
            </button>
          )}
          <button
            type="button"
            disabled={!canDelete}
            onClick={() => setConfirmDelete(true)}
            title={canDelete ? 'Delete this run' : 'Run is still in progress'}
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-red-700 disabled:opacity-40 disabled:hover:text-slate-500"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1">
              {KIND_LABEL[run.kind] || run.kind}
            </div>
            <h1 className="text-xl font-semibold text-slate-900 break-all">
              {moduleLabel(run.module_or_profile)}
            </h1>
            <div className="text-[11px] text-slate-500 mt-0.5 font-mono">{run.module_or_profile}</div>
            <div className="text-sm text-slate-500 mt-2 break-all font-mono">
              target: {run.target || '-'}
            </div>
          </div>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs ${pill.color}`}>
            <Icon className="w-3.5 h-3.5" />
            {pill.label}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Stat label="Started" value={fmt(run.started_at)} />
          <Stat label="Completed" value={fmt(run.completed_at)} />
          <Stat label="Created" value={fmt(run.created_at)} />
          <Stat label="Findings" value={run.finding_count} mono />
        </div>

        {Array.isArray(run.engines) && run.engines.length > 0 && (
          <EnginesStrip engines={run.engines} pentestId={pentestId} />
        )}

        {!TERMINAL_STATUSES.has(run.status) && <RunProgressLine progress={run.progress} />}

        {run.error_message && (
          <div className="mt-3 rounded-lg border border-red-300 bg-red-100 px-3 py-2 text-sm text-red-700 break-all">
            {run.error_message}
          </div>
        )}
      </div>

      <ViewComponent run={run} results={data.results} />

      {data.results_total > pageSize && (
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>
            Page {page} of {totalPages} - {data.results_total.toLocaleString()} total
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="inline-flex items-center gap-1 px-3 py-1 rounded border border-cyber-300/40 hover:border-cyber-300 disabled:opacity-40"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              Prev
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="inline-flex items-center gap-1 px-3 py-1 rounded border border-cyber-300/40 hover:border-cyber-300 disabled:opacity-40"
            >
              Next
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmDelete}
        onClose={() => !deleting && setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Delete run"
        message={`Delete this ${KIND_LABEL[run.kind] || run.kind} run? Findings only seen in this run will be removed; findings seen across multiple runs will be preserved.`}
        confirmText={deleting ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
        variant="danger"
      />

      <ConfirmModal
        isOpen={confirmStop}
        onClose={() => !stopping && setConfirmStop(false)}
        onConfirm={handleStop}
        title="Stop scan"
        message="Stop this vulnerability scan? Active beacon tasks will be cancelled and any findings collected so far are kept."
        confirmText={stopping ? 'Stopping...' : 'Stop'}
        cancelText="Cancel"
        variant="warning"
      />
    </div>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-slate-800 ${mono ? 'font-mono' : ''}`}>{value}</div>
    </div>
  );
}


// ----- Engines strip (multi-engine VulnScanRun) -----
//
// Shows a pill per engine that participated in this run with its own status
// and results_count. Each pill links to a filtered Findings tab view (ZAP
// findings flow into PentestFinding via extract_zap, so the operator picks
// them up there rather than in the result table on this page).

const ENGINE_LABEL = { nuclei: 'Nuclei', zap: 'OWASP ZAP' };
const ENGINE_PILL_COLOR = {
  nuclei: 'border-wasabi-300 bg-wasabi-100 text-wasabi-700',
  zap:    'border-orange-300 bg-orange-100 text-orange-700',
};
const ENGINE_STATUS_TEXT = {
  pending:   'pending',
  running:   'running',
  completed: 'done',
  failed:    'failed',
};

function EnginesStrip({ engines, pentestId }) {
  const hasZap = engines.some((e) => e.engine === 'zap');
  return (
    <div className="mt-4 rounded-lg border border-cyber-300/30 bg-cyber-100/40 p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
        Engines ({engines.length})
      </div>
      <div className="flex flex-wrap gap-2">
        {engines.map((e) => {
          const cls = ENGINE_PILL_COLOR[e.engine] || 'border-cyber-300/40 bg-cyber-100 text-slate-700';
          return (
            <div
              key={e.id}
              className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-lg border ${cls} text-xs`}
              title={e.error || ''}
            >
              <span className="font-medium">{ENGINE_LABEL[e.engine] || e.engine}</span>
              <span className="text-slate-500 normal-case">/</span>
              <span className="text-slate-700">{e.profile_name || '-'}</span>
              <span className="text-slate-500 normal-case">/</span>
              <span className="uppercase tracking-wider text-[10px] font-bold">{ENGINE_STATUS_TEXT[e.status] || e.status}</span>
              <span className="text-slate-500 normal-case">/</span>
              <span className="font-mono">{e.results_count} findings</span>
              <span className="text-slate-500 text-[10px]">({e.completed_task_count}/{e.beacon_task_count} tasks)</span>
            </div>
          );
        })}
      </div>
      {hasZap && (
        <div className="mt-2 text-[11px] text-slate-500">
          ZAP alerts surface in the{' '}
          <Link to={`/pentests/${pentestId}/findings`} className="underline hover:text-shinobi-green">
            Findings tab
          </Link>
          {' '}with the Auto badge. Nuclei VulnFinding rows render below.
        </div>
      )}
    </div>
  );
}
